// src/components/RPA/buttons/GenerateWorkpaperButton.js
// Workpaper button + modal with two tabs:
//   📋 Workpapers — all sections with sign-off chain per section (Diligent-style)
//   ⚠️  Issues     — raise, track, comment on, close audit issues
//
// Drop-in replacement. Props unchanged: docId, onDone.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// ─────────────────────────────────────────────────────────────────────────────
// FRONTEND ROLE/SIGNOFF CONSTANTS (mirrors constants/auditRoles.js)
// ─────────────────────────────────────────────────────────────────────────────
const AUDIT_ROLE_LABELS = {
  partner:            'Engagement Partner',
  audit_manager:      'Audit Manager',
  senior_auditor:     'Senior Auditor',
  auditor:            'Auditor',
  reviewer:           'Reviewer',
  oversight_reviewer: 'Oversight Reviewer',
};

const SIGNOFF_TYPE_LABELS = {
  preparer:         'Preparer',
  detail_reviewer:  'Detail Reviewer',
  general_reviewer: 'General Reviewer',
  partner:          'Engagement Partner',
};

const ISSUE_TYPE_LABELS = {
  material_weakness:       'Material Weakness',
  significant_deficiency:  'Significant Deficiency',
  finding:                 'Finding',
  observation:             'Observation',
  control_deficiency:      'Control Deficiency',
  compliance_violation:    'Compliance Violation',
  other:                   'Other',
};

const SEVERITY_COLORS = {
  critical: '#991b1b',
  high:     '#b45309',
  moderate: '#1e40af',
  low:      '#374151',
};

const STATUS_COLORS = {
  open:           '#dc2626',
  in_remediation: '#d97706',
  pending_review: '#2563eb',
  closed:         '#059669',
  escalated:      '#7c3aed',
  voided:         '#9ca3af',
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLE TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    overflowY: 'auto', padding: '24px 16px',
  },
  modal: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 940,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 24px 0', borderBottom: '1px solid #e5e7eb',
    position: 'sticky', top: 0, background: '#fff', zIndex: 10, borderRadius: '12px 12px 0 0',
  },
  body: { padding: '16px 24px 32px' },
  closeBtn: {
    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '5px 12px', cursor: 'pointer', fontSize: 13, color: '#374151',
  },
  triggerBtn: {
    background: '#7c3aed', color: '#fff', border: 'none',
    padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  refreshBtn: {
    background: 'none', border: '1px solid #7c3aed', color: '#7c3aed',
    padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
  },
  tab: (active) => ({
    padding: '8px 16px', marginRight: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
    color: active ? '#7c3aed' : '#6b7280', background: 'none', border: 'none',
    borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
  }),
  sectionCard: {
    border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 14px', cursor: 'pointer', userSelect: 'none', background: '#f9fafb',
  },
  sectionBody: { padding: '12px 16px', borderTop: '1px solid #e5e7eb' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 600,
    background: color === 'green' ? '#d1fae5' : color === 'red' ? '#fee2e2' : color === 'yellow' ? '#fef3c7' : color === 'blue' ? '#dbeafe' : color === 'purple' ? '#ede9fe' : '#f3f4f6',
    border: `1px solid ${color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'yellow' ? '#f59e0b' : color === 'blue' ? '#3b82f6' : color === 'purple' ? '#8b5cf6' : '#d1d5db'}`,
    color: color === 'green' ? '#065f46' : color === 'red' ? '#991b1b' : color === 'yellow' ? '#92400e' : color === 'blue' ? '#1e40af' : color === 'purple' ? '#5b21b6' : '#374151',
  }),
  exceptionCard: {
    background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: 10, marginBottom: 8,
  },
  checkpointCard: {
    background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: 12,
  },
  dataRow: { display: 'flex', gap: 6, marginBottom: 4, fontSize: 12 },
  label: { color: '#6b7280', minWidth: 160 },
  value: { color: '#111827', fontWeight: 500 },
  priorityBanner: {
    background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 14,
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 14,
  },
  summaryTile: {
    border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', textAlign: 'center',
  },
  // Sign-off panel
  signoffPanel: {
    marginTop: 14, borderTop: '2px solid #e5e7eb', paddingTop: 12,
  },
  signoffRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6,
    padding: '7px 10px', borderRadius: 6,
  },
  signoffBtn: {
    background: '#7c3aed', color: '#fff', border: 'none',
    padding: '3px 10px', borderRadius: 4, fontSize: 10, cursor: 'pointer', fontWeight: 600,
    flexShrink: 0,
  },
  retractBtn: {
    background: 'none', border: '1px solid #d1d5db', color: '#6b7280',
    padding: '3px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer', flexShrink: 0,
  },
  notesInput: {
    width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
    fontSize: 11, marginTop: 5, resize: 'vertical', minHeight: 38, boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  // Issues panel
  issueCard: {
    border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
  },
  issueHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '10px 14px', cursor: 'pointer', background: '#f9fafb',
  },
  issueBody: { padding: '12px 16px', borderTop: '1px solid #e5e7eb' },
  textarea: {
    width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px',
    fontSize: 12, resize: 'vertical', minHeight: 70, boxSizing: 'border-box',
    fontFamily: 'inherit', lineHeight: 1.5,
  },
  input: {
    width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
    fontSize: 12, boxSizing: 'border-box', fontFamily: 'inherit',
  },
  select: {
    border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
    fontSize: 12, background: '#fff', cursor: 'pointer',
  },
  actionBtn: (color) => ({
    background: color || '#1d4ed8', color: '#fff', border: 'none',
    padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 500,
  }),
  ghostBtn: {
    background: 'none', border: '1px solid #d1d5db', color: '#374151',
    padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
  },
  // Signature button in modal header — green when signature saved, yellow when not set
  sigBtn: (hasSig) => ({
    background: hasSig ? '#f0fdf4' : '#fef3c7',
    border: `1px solid ${hasSig ? '#22c55e' : '#f59e0b'}`,
    color: hasSig ? '#065f46' : '#92400e',
    padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: 4,
  }),
  commentBubble: {
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '8px 12px', marginBottom: 6,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const statusColor = (s) => s === 'complete' ? 'green' : s === 'exception' ? 'red' : s === 'incomplete' ? 'yellow' : 'grey';
const statusLabel = (s) => s === 'complete' ? '✅ Complete' : s === 'exception' ? '⚠️ Exception' : s === 'incomplete' ? '⏸ Incomplete' : s === 'error' ? '❌ Error' : s || '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const fmtShort = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// Build a stable checkbox ID from section + field index
const cbId = (sectionId, idx) => `${sectionId}__cb${idx}`;

// ─────────────────────────────────────────────────────────────────────────────
// SIGNATURE PAD — canvas-based drawing component
// ─────────────────────────────────────────────────────────────────────────────
const SignaturePad = ({ currentSignature, onSave, onClose }) => {
  const canvasRef  = useRef(null);
  const isDrawing  = useRef(false);
  const lastPos    = useRef({ x: 0, y: 0 });
  const [isEmpty,  setIsEmpty]  = useState(!currentSignature);
  const [showExisting, setShowExisting] = useState(!!currentSignature);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches?.[0] || e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setShowExisting(false);
    setIsEmpty(false);
    const canvas = canvasRef.current;
    isDrawing.current = true;
    lastPos.current   = getPos(e, canvas);
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = () => { isDrawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    setShowExisting(false);
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (isEmpty && !showExisting) {
      alert('Please draw your signature before saving.');
      return;
    }
    const data = showExisting ? currentSignature : canvas.toDataURL('image/png');
    onSave(data);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>✍ My Signature</h3>
            <p style={{ margin: '3px 0 0', fontSize: 11, color: '#6b7280' }}>Draw in the box below. Used on workpaper checkpoints and sign-offs.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        {currentSignature && showExisting && (
          <div style={{ border: '2px solid #86efac', borderRadius: 8, background: '#f0fdf4', padding: 12, marginBottom: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#065f46', marginBottom: 6 }}>Current saved signature:</div>
            <img src={currentSignature} alt="Saved signature" style={{ maxHeight: 80, maxWidth: '100%' }} />
            <div style={{ marginTop: 8 }}>
              <button onClick={() => { setShowExisting(false); setIsEmpty(true); const canvas = canvasRef.current; canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height); }} style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Replace with new drawing
              </button>
            </div>
          </div>
        )}

        <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, background: '#f9fafb', position: 'relative', display: showExisting ? 'none' : 'block' }}>
          <canvas
            ref={canvasRef}
            width={440}
            height={160}
            style={{ display: 'block', cursor: 'crosshair', touchAction: 'none', width: '100%', borderRadius: 6 }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {isEmpty && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <span style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>Draw your signature here</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={clear} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#374151' }}>
            Clear
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#374151' }}>Cancel</button>
            <button onClick={save} style={{ padding: '7px 18px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Save Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SIGN-OFF PANEL — per workpaper section
// ─────────────────────────────────────────────────────────────────────────────
const SignoffPanel = ({ sectionId, sectionSignoffs = {}, sectionAuthority, onSignoff, onRetract }) => {
  const [notesOpen, setNotesOpen] = useState(null);
  const [notes, setNotes]         = useState('');
  const [busy, setBusy]           = useState(false);

  const chain          = sectionAuthority?.chain || [];
  const nextPending    = sectionAuthority?.nextPending;
  const canSign        = sectionAuthority?.canSign;
  const cantReason     = sectionAuthority?.cannotSignReason;
  const retractable    = sectionAuthority?.retractable || {};
  const fullySignedOff = sectionAuthority?.fullySignedOff;

  if (!chain.length) return null;

  const handleSign = async (signoffType) => {
    setBusy(true);
    try { await onSignoff(sectionId, signoffType, notes); setNotesOpen(null); setNotes(''); }
    finally { setBusy(false); }
  };

  const handleRetract = async (signoffType) => {
    if (!window.confirm(`Retract your ${SIGNOFF_TYPE_LABELS[signoffType]} sign-off?`)) return;
    setBusy(true);
    try { await onRetract(sectionId, signoffType); }
    finally { setBusy(false); }
  };

  return (
    <div style={s.signoffPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🖊 Sign-Off Chain</span>
        {fullySignedOff ? <span style={s.badge('green')}>✅ Fully Signed Off</span> : <span style={s.badge('yellow')}>⏳ Pending</span>}
      </div>

      {chain.map((type) => {
        const existing   = sectionSignoffs[type];
        const isSigned   = !!existing?.signedAt;
        const isNext     = type === nextPending;
        const isMyTurn   = isNext && canSign;
        const isLocked   = !isSigned && !isNext;
        const canRetract = retractable[type] === true;

        let bg = '#f9fafb', icon = '🔒';
        if (isSigned)                    { bg = '#f0fdf4'; icon = '✅'; }
        else if (isMyTurn)               { bg = '#eff6ff'; icon = '⏳'; }
        else if (isNext && !canSign)     { bg = '#fafafa'; icon = '🚫'; }

        return (
          <div key={type} style={{ ...s.signoffRow, background: bg }}>
            <span style={{ fontSize: 15, width: 20, flexShrink: 0, marginTop: 1 }}>{icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{SIGNOFF_TYPE_LABELS[type]}</div>
              {isSigned ? (
                <>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
                    {existing.name}
                    {existing.auditRole ? ` · ${AUDIT_ROLE_LABELS[existing.auditRole] || existing.auditRole}` : ''}
                    {' · '}{fmtDate(existing.signedAt)}
                  </div>
                  {existing.notes && <div style={{ fontSize: 10, color: '#4b5563', fontStyle: 'italic', marginTop: 1 }}>"{existing.notes}"</div>}
                  {/* Embedded signature image — stored at sign-off time */}
                  {existing.signature && (
                    <div style={{ marginTop: 4 }}>
                      <img
                        src={existing.signature}
                        alt={`${existing.name} signature`}
                        style={{ maxHeight: 36, maxWidth: 160, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', padding: '2px 4px' }}
                      />
                    </div>
                  )}
                </>
              ) : isLocked ? (
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>Waiting on prior sign-off</div>
              ) : isNext && !canSign ? (
                <div style={{ fontSize: 10, color: '#b45309', marginTop: 1 }}>{cantReason}</div>
              ) : isMyTurn ? (
                <>
                  <div style={{ fontSize: 10, color: '#1d4ed8', marginTop: 1 }}>Your signature required</div>
                  {notesOpen === type && (
                    <textarea style={s.notesInput} placeholder="Optional reviewer notes…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                  )}
                </>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
              {isSigned && canRetract && (
                <button style={s.retractBtn} onClick={() => handleRetract(type)} disabled={busy}>Retract</button>
              )}
              {isMyTurn && (
                <>
                  <button style={{ ...s.retractBtn, color: '#374151' }} onClick={() => { setNotesOpen(notesOpen === type ? null : type); if (notesOpen === type) setNotes(''); }} disabled={busy}>
                    {notesOpen === type ? 'Cancel' : '📝'}
                  </button>
                  <button style={s.signoffBtn} onClick={() => handleSign(type)} disabled={busy}>
                    {busy ? '…' : 'Sign Off ▸'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WORKPAPER SECTION — collapsible card with sign-off panel
// ─────────────────────────────────────────────────────────────────────────────
const WorkpaperSection = ({
  section, sectionSignoffs, sectionAuthority, onSignoff, onRetract,
  checkboxStates, onCheckboxToggle, userSignature, currentAdminId,
}) => {
  const [open, setOpen] = useState(false);
  if (!section) return null;

  const color           = statusColor(section.status);
  const hasExceptions   = section.exceptions?.length > 0;
  const hasCheckpoint   = !!section.verification_checkpoint;
  const hasResults      = section.results && Object.keys(section.results).length > 0;
  const isFullySigned   = sectionAuthority?.fullySignedOff;
  const nextPending     = sectionAuthority?.nextPending;
  const sId             = section.section_id;

  // Derive checkpoint completion state
  const checkpointFields = section.verification_checkpoint?.fields || [];
  const sectionCheckboxes = checkpointFields.map((_, i) => checkboxStates?.[cbId(sId, i)] || null);
  const checkedCount      = sectionCheckboxes.filter(c => c?.checked).length;
  const allChecked        = checkpointFields.length > 0 && checkedCount === checkpointFields.length;

  // Find the most recent checker in this section (for signature line)
  const mostRecentChecker = sectionCheckboxes
    .filter(Boolean)
    .sort((a, b) => new Date(b.checkedAt) - new Date(a.checkedAt))[0];
  const currentUserCheckedAny = sectionCheckboxes.some(c => c?.checkedBy === currentAdminId);

  const renderValue = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) {
      if (!val.length) return '—';
      if (typeof val[0] === 'string') return val.join(', ');
      return (
        <div style={{ marginTop: 4 }}>
          {val.map((item, i) => (
            <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 6, marginBottom: 4, fontSize: 11 }}>
              {typeof item === 'object'
                ? Object.entries(item).map(([k, v]) => (
                    <div key={k} style={s.dataRow}>
                      <span style={s.label}>{k.replace(/_/g, ' ')}:</span>
                      <span style={s.value}>{String(v ?? '—')}</span>
                    </div>
                  ))
                : String(item)}
            </div>
          ))}
        </div>
      );
    }
    if (typeof val === 'object') {
      return (
        <div style={{ marginTop: 4 }}>
          {Object.entries(val).map(([k, v]) => (
            <div key={k} style={s.dataRow}>
              <span style={s.label}>{k.replace(/_/g, ' ')}:</span>
              <span style={{ ...s.value, flex: 1 }}>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}</span>
            </div>
          ))}
        </div>
      );
    }
    return String(val);
  };

  return (
    <div style={s.sectionCard}>
      <div style={s.sectionHeader} onClick={() => setOpen(!open)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
            {section.section_id} — {section.title}
          </span>
          {section.reference && <span style={{ fontSize: 10, color: '#6b7280', fontStyle: 'italic' }}>{section.reference}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isFullySigned
            ? <span style={s.badge('green')}>✅ Signed</span>
            : nextPending
              ? <span style={s.badge('blue')}>🖊 {SIGNOFF_TYPE_LABELS[nextPending]}</span>
              : null}
          {hasCheckpoint && (
            <span style={s.badge(allChecked ? 'green' : 'grey')}>
              {checkedCount}/{checkpointFields.length} ✓
            </span>
          )}
          {hasExceptions && <span style={s.badge('red')}>{section.exceptions.length} exception{section.exceptions.length !== 1 ? 's' : ''}</span>}
          <span style={s.badge(color)}>{statusLabel(section.status)}</span>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={s.sectionBody}>
          {(section.status === 'incomplete' || section.status === 'error') && section.reason && (
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12, color: '#92400e' }}>
              ℹ️ {section.reason}
            </div>
          )}
          {section.purpose && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Purpose</div>
              <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6, background: '#f9fafb', padding: 8, borderRadius: 4 }}>{section.purpose}</div>
            </div>
          )}
          {section.procedure_performed && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Procedures Performed</div>
              <pre style={{ fontSize: 11, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 10, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.6 }}>
                {section.procedure_performed}
              </pre>
            </div>
          )}
          {section.findings_narrative && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Auditor's Opinion</div>
              <div style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: 10, lineHeight: 1.7 }}>{section.findings_narrative}</div>
            </div>
          )}
          {hasResults && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Results</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                {Object.entries(section.results).map(([key, val]) => {
                  const isComplex = Array.isArray(val)
                    ? (val.length > 0 && typeof val[0] === 'object')
                    : (val !== null && typeof val === 'object');
                  return isComplex ? (
                    // Complex values (object/array-of-objects) get a block layout so divs don't nest in spans
                    <div key={key} style={{ marginBottom: 6 }}>
                      <div style={{ ...s.label, marginBottom: 2 }}>{key.replace(/_/g, ' ')}:</div>
                      <div style={{ paddingLeft: 8 }}>{renderValue(val)}</div>
                    </div>
                  ) : (
                    <div key={key} style={s.dataRow}>
                      <span style={s.label}>{key.replace(/_/g, ' ')}:</span>
                      <span style={{ ...s.value, flex: 1 }}>{renderValue(val)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {hasExceptions && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>⚠️ Exceptions ({section.exceptions.length})</div>
              {section.exceptions.map((ex, i) => (
                <div key={i} style={s.exceptionCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#991b1b' }}>{ex.type}</span>
                    <span style={s.badge(ex.severity === 'HIGH' || ex.severity === 'MATERIAL' ? 'red' : 'yellow')}>{ex.severity}</span>
                  </div>
                  {(ex.account || ex.customer || ex.warehouse || ex.bank) && (
                    <div style={{ fontSize: 11, color: '#374151', marginBottom: 3 }}>
                      <strong>{ex.account || ex.customer || ex.warehouse || ex.bank}</strong>
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#374151', marginBottom: 5, lineHeight: 1.5 }}>{ex.detail}</div>
                  {ex.amount && ex.amount !== '$0' && (
                    <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 5 }}><strong>Amount:</strong> {ex.amount}</div>
                  )}
                  <div style={{ fontSize: 11, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: 6, lineHeight: 1.5 }}>
                    <strong>Action Required:</strong> {ex.action_required}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── VERIFICATION CHECKPOINT — functional checkboxes ── */}
          {hasCheckpoint && (
            <div style={s.checkpointCard}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
                ✋ Auditor Verification Checkpoint
                {checkpointFields.length > 0 && (
                  <span style={{ ...s.badge(allChecked ? 'green' : 'blue'), marginLeft: 8 }}>
                    {checkedCount}/{checkpointFields.length}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#1e3a8a', marginBottom: 8, lineHeight: 1.6 }}>
                {section.verification_checkpoint.instruction}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {checkpointFields.map((field, i) => {
                  const id    = cbId(sId, i);
                  const state = checkboxStates?.[id];
                  const isChecked = !!state?.checked;

                  return (
                    <div key={i}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => onCheckboxToggle && onCheckboxToggle(id, sId, field, e.target.checked)}
                          style={{ width: 16, height: 16, marginTop: 1, cursor: 'pointer', accentColor: '#3b82f6', flexShrink: 0 }}
                        />
                        <div>
                          <span style={{ fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>{field}</span>
                          {isChecked && state && (
                            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                              ✓ {state.checkedByName} · {fmtShort(state.checkedAt)}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>

              {/* ── Signature line ── */}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #bfdbfe', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  {currentUserCheckedAny && userSignature ? (
                    <div>
                      <img
                        src={userSignature}
                        alt="Auditor signature"
                        style={{ maxHeight: 44, maxWidth: 180, border: '1px solid #bfdbfe', borderRadius: 4, background: '#fff', padding: '2px 6px' }}
                      />
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                      Signature: _________________________
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
                  Date: {mostRecentChecker?.checkedAt ? fmtShort(mostRecentChecker.checkedAt) : '_____________'}
                </div>
                {!userSignature && checkedCount > 0 && (
                  <div style={{ fontSize: 10, color: '#b45309', fontStyle: 'italic' }}>
                    Set your signature to display here
                  </div>
                )}
              </div>
            </div>
          )}

          {section.completed_at && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>Completed: {section.completed_at}</div>
          )}

          {/* ── SIGN-OFF PANEL ── */}
          {sectionAuthority && (
            <SignoffPanel
              sectionId={section.section_id}
              sectionSignoffs={sectionSignoffs || {}}
              sectionAuthority={sectionAuthority}
              onSignoff={onSignoff}
              onRetract={onRetract}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ISSUE DETAIL PANEL (expanded view inside issues tab)
// ─────────────────────────────────────────────────────────────────────────────
const IssueDetail = ({ issue, docId, userRole, onRefresh }) => {
  const [mgmtText,       setMgmtText]       = useState(issue.managementResponse?.text || '');
  const [remPlan,        setRemPlan]         = useState(issue.remediationPlan?.text || '');
  const [remDate,        setRemDate]         = useState(issue.remediationPlan?.targetDate ? issue.remediationPlan.targetDate.slice(0, 10) : '');
  const [commentText,    setCommentText]     = useState('');
  const [commentInternal,setCommentInternal] = useState(false);
  const [elevSeverity,   setElevSeverity]    = useState('high');
  const [elevReason,     setElevReason]      = useState('');
  const [closureNotes,   setClosureNotes]    = useState('');
  const [busy,           setBusy]            = useState('');

  const api = (method, path, data) =>
    axios[method](`${API_URL}/admin/rpa/docs/${docId}/issues/${issue._id}${path}`, data, { withCredentials: true });

  const save = async (label, fn) => {
    setBusy(label);
    try { await fn(); await onRefresh(); } catch (e) { alert(e?.response?.data?.error || e.message); }
    finally { setBusy(''); }
  };

  const visibleComments = (issue.comments || []).filter(c => !c.deletedAt);

  return (
    <div>
      {/* Management Response */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Management Response</div>
        <textarea
          style={s.textarea}
          placeholder="Enter management's written response to this finding…"
          value={mgmtText}
          onChange={e => setMgmtText(e.target.value)}
          rows={3}
        />
        {issue.managementResponse?.respondedBy && (
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3 }}>
            Last updated: {issue.managementResponse.respondedBy?.name} · {fmtDate(issue.managementResponse.lastUpdatedAt || issue.managementResponse.respondedAt)}
          </div>
        )}
        <button
          style={{ ...s.actionBtn('#1d4ed8'), marginTop: 6 }}
          disabled={busy === 'mgmt'}
          onClick={() => save('mgmt', () => api('patch', '/management-response', { text: mgmtText }))}
        >
          {busy === 'mgmt' ? 'Saving…' : 'Save Response'}
        </button>
      </div>

      {/* Remediation Plan */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Remediation Plan</div>
        <textarea
          style={s.textarea}
          placeholder="Describe the corrective action plan and steps to be taken…"
          value={remPlan}
          onChange={e => setRemPlan(e.target.value)}
          rows={3}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>Target Date:</label>
          <input type="date" style={{ ...s.input, width: 160 }} value={remDate} onChange={e => setRemDate(e.target.value)} />
          <button
            style={s.actionBtn('#1d4ed8')}
            disabled={busy === 'rem'}
            onClick={() => save('rem', () => api('patch', '/remediation-plan', { text: remPlan, targetDate: remDate || null }))}
          >
            {busy === 'rem' ? 'Saving…' : 'Save Plan'}
          </button>
        </div>
      </div>

      {/* Comments */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
          Comments ({visibleComments.length})
        </div>
        {visibleComments.length > 0 && (
          <div style={{ marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
            {visibleComments.map(c => (
              <div key={c._id} style={s.commentBubble}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
                    {c.author?.name || 'Unknown'}
                    {c.isInternal && <span style={{ ...s.badge('purple'), marginLeft: 4 }}>Internal</span>}
                  </span>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(c.createdAt)}</span>
                </div>
                <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.5 }}>{c.text}</div>
              </div>
            ))}
          </div>
        )}
        <textarea
          style={s.textarea}
          placeholder="Add a comment…"
          value={commentText}
          onChange={e => setCommentText(e.target.value)}
          rows={2}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={commentInternal} onChange={e => setCommentInternal(e.target.checked)} />
            Internal only
          </label>
          <button
            style={s.actionBtn('#374151')}
            disabled={busy === 'comment' || !commentText.trim()}
            onClick={() => save('comment', async () => {
              await api('post', '/comments', { text: commentText, isInternal: commentInternal });
              setCommentText('');
            })}
          >
            {busy === 'comment' ? '…' : 'Add Comment'}
          </button>
        </div>
      </div>

      {/* Elevate + Close actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
        {issue.status !== 'closed' && (
          <>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select style={s.select} value={elevSeverity} onChange={e => setElevSeverity(e.target.value)}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="moderate">Moderate</option>
                <option value="low">Low</option>
              </select>
              <button
                style={s.actionBtn('#7c3aed')}
                disabled={busy === 'elevate'}
                onClick={() => {
                  const reason = window.prompt('Reason for elevation (optional):') || '';
                  save('elevate', () => api('post', '/elevate', { toSeverity: elevSeverity, reason }));
                }}
              >
                {busy === 'elevate' ? '…' : '↑ Elevate'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                style={{ ...s.input, width: 180 }}
                placeholder="Closure notes…"
                value={closureNotes}
                onChange={e => setClosureNotes(e.target.value)}
              />
              <button
                style={s.actionBtn('#059669')}
                disabled={busy === 'close'}
                onClick={() => save('close', () => api('post', '/close', { closureNotes }))}
              >
                {busy === 'close' ? '…' : '✓ Close Issue'}
              </button>
            </div>
          </>
        )}
        {issue.status === 'closed' && (
          <button
            style={s.actionBtn('#d97706')}
            disabled={busy === 'reopen'}
            onClick={() => save('reopen', () => api('post', '/reopen', {}))}
          >
            {busy === 'reopen' ? '…' : '↩ Reopen'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ISSUES TAB
// ─────────────────────────────────────────────────────────────────────────────
const IssuesTab = ({ docId, userRole }) => {
  const [issues,    setIssues]    = useState([]);
  const [stats,     setStats]     = useState({});
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [showForm,  setShowForm]  = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({ title: '', description: '', type: 'finding', severity: 'moderate', priority: 'medium', workpaperRef: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/issues${params}`, { withCredentials: true });
      setIssues(res.data?.issues || []);
      setStats(res.data?.stats || {});
    } catch (e) { setErr('Failed to load issues'); }
    finally { setLoading(false); }
  }, [docId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const createIssue = async () => {
    if (!form.title.trim()) { setErr('Title is required'); return; }
    setSaving(true); setErr('');
    try {
      await axios.post(`${API_URL}/admin/rpa/docs/${docId}/issues`, {
        ...form,
        amount: form.amount ? Number(form.amount) : null,
        workpaperRef: form.workpaperRef || null,
      }, { withCredentials: true });
      setForm({ title: '', description: '', type: 'finding', severity: 'moderate', priority: 'medium', workpaperRef: '', amount: '' });
      setShowForm(false);
      await load();
    } catch (e) { setErr(e?.response?.data?.error || 'Failed to create issue'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Total',      value: stats.total      || 0, color: '#374151' },
          { label: 'Open',       value: stats.open       || 0, color: '#dc2626' },
          { label: 'Remediation',value: stats.in_remediation || 0, color: '#d97706' },
          { label: 'Closed',     value: stats.closed     || 0, color: '#059669' },
          { label: 'Critical',   value: stats.critical   || 0, color: '#7c3aed' },
        ].map(t => (
          <div key={t.label} style={{ ...s.summaryTile, minWidth: 70, padding: '6px 10px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>{t.label}</div>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <select style={{ ...s.select, fontSize: 11 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_remediation">In Remediation</option>
            <option value="closed">Closed</option>
            <option value="escalated">Escalated</option>
          </select>
          <button style={s.actionBtn('#7c3aed')} onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Raise Issue'}
          </button>
        </div>
      </div>

      {/* Raise issue form */}
      {showForm && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 14, background: '#f9fafb' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 10 }}>Raise New Issue</div>
          {err && <div style={{ color: '#991b1b', fontSize: 11, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Title *</label>
              <input style={s.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief issue title…" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Type</label>
              <select style={{ ...s.select, width: '100%' }} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Severity</label>
              <select style={{ ...s.select, width: '100%' }} value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="moderate">Moderate</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Workpaper Ref</label>
              <input style={s.input} value={form.workpaperRef} onChange={e => setForm(f => ({ ...f, workpaperRef: e.target.value }))} placeholder="e.g. WP-300" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Dollar Amount</label>
              <input style={s.input} type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Description</label>
              <textarea style={s.textarea} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue, its source, and impact…" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.actionBtn('#dc2626')} onClick={createIssue} disabled={saving}>{saving ? 'Raising…' : 'Raise Issue'}</button>
            <button style={s.ghostBtn} onClick={() => { setShowForm(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Issue list */}
      {loading && <div style={{ textAlign: 'center', padding: 30, color: '#6b7280' }}>Loading issues…</div>}
      {!loading && issues.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
          No issues found. Use "Raise Issue" to document an audit finding.
        </div>
      )}
      {!loading && issues.map(issue => (
        <div key={issue._id} style={s.issueCard}>
          <div style={s.issueHeader} onClick={() => setExpanded(expanded === issue._id ? null : issue._id)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>#{issue.issueNumber}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{issue.title}</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                <span style={{ ...s.badge('grey'), background: `${STATUS_COLORS[issue.status]}18`, border: `1px solid ${STATUS_COLORS[issue.status]}40`, color: STATUS_COLORS[issue.status] }}>
                  {issue.status?.replace(/_/g, ' ')}
                </span>
                <span style={{ ...s.badge('grey'), color: SEVERITY_COLORS[issue.severity] }}>{issue.severity}</span>
                <span style={s.badge('grey')}>{ISSUE_TYPE_LABELS[issue.type] || issue.type}</span>
                {issue.workpaperRef && <span style={s.badge('blue')}>{issue.workpaperRef}</span>}
                {issue.amount > 0 && <span style={s.badge('red')}>${issue.amount.toLocaleString()}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 10 }}>
              {(issue.comments || []).filter(c => !c.deletedAt).length > 0 && (
                <span style={{ fontSize: 10, color: '#6b7280' }}>💬 {(issue.comments || []).filter(c => !c.deletedAt).length}</span>
              )}
              <span style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(issue.createdAt)}</span>
              <span style={{ fontSize: 13, color: '#9ca3af' }}>{expanded === issue._id ? '▲' : '▼'}</span>
            </div>
          </div>
          {expanded === issue._id && (
            <div style={s.issueBody}>
              {issue.description && (
                <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6, marginBottom: 12, background: '#f9fafb', padding: 8, borderRadius: 4 }}>
                  {issue.description}
                </div>
              )}
              <IssueDetail issue={issue} docId={docId} userRole={userRole} onRefresh={load} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────
const WorkpaperModal = ({ docId, onClose }) => {
  const [tab,            setTab]           = useState('workpapers');
  const [wpData,         setWpData]        = useState(null);
  const [signoffData,    setSignoffData]   = useState(null);
  const [checkboxStates, setCheckboxStates]= useState({});
  const [mySignature,    setMySignature]   = useState(null);   // current user's saved signature data URL
  const [currentAdminId, setCurrentAdminId]= useState(null);
  const [showSigPad,     setShowSigPad]    = useState(false);
  const [loading,        setLoading]       = useState(true);
  const [error,          setError]         = useState('');
  const [signoffErr,     setSignoffErr]    = useState('');

  // ── Data loaders ────────────────────────────────────────────────────────
  const loadWorkpapers = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers`, { withCredentials: true });
      setWpData(res.data?.workpaper_package || null);
    } catch (e) { setError(e?.response?.data?.message || 'Failed to load workpaper package.'); }
    finally { setLoading(false); }
  }, [docId]);

  const loadSignoffs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers/signoffs`, { withCredentials: true });
      setSignoffData(res.data || null);
      if (res.data?.actorId) setCurrentAdminId(res.data.actorId);
    } catch { /* non-fatal */ }
  }, [docId]);

  const loadCheckboxes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers/checkboxes`, { withCredentials: true });
      setCheckboxStates(res.data?.checkboxStates || {});
    } catch { /* non-fatal — checkboxes degrade gracefully */ }
  }, [docId]);

  const loadMySignature = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/profile/signature`, { withCredentials: true });
      setMySignature(res.data?.signature?.data || null);
    } catch {
      setMySignature(null); // 404 = no signature yet, that's fine
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadWorkpapers(), loadSignoffs(), loadCheckboxes(), loadMySignature()]);
  }, [loadWorkpapers, loadSignoffs, loadCheckboxes, loadMySignature]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Checkbox toggle ──────────────────────────────────────────────────────
  const handleCheckboxToggle = useCallback(async (checkboxId, sectionId, label, checked) => {
    // Optimistic update
    setCheckboxStates(prev => {
      const next = { ...prev };
      if (checked) {
        next[checkboxId] = { checked: true, checkboxId, sectionId, label, checkedByName: 'You', checkedAt: new Date().toISOString() };
      } else {
        delete next[checkboxId];
      }
      return next;
    });

    try {
      await axios.patch(
        `${API_URL}/admin/rpa/docs/${docId}/workpapers/checkboxes/${checkboxId}`,
        { checked, sectionId, label },
        { withCredentials: true }
      );
      // Reload to get server-confirmed state (with real adminId + name)
      await loadCheckboxes();
    } catch (e) {
      // Revert optimistic update on failure
      await loadCheckboxes();
      alert(e?.response?.data?.error || 'Failed to update checkbox');
    }
  }, [docId, loadCheckboxes]);

  // ── Sign-off handlers ────────────────────────────────────────────────────
  const handleSignoff = useCallback(async (sectionId, signoffType, notes) => {
    setSignoffErr('');
    try {
      await axios.post(`${API_URL}/admin/rpa/docs/${docId}/workpapers/${sectionId}/signoff`, { signoffType, notes }, { withCredentials: true });
      await loadSignoffs();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Sign-off failed.';
      setSignoffErr(msg); throw e;
    }
  }, [docId, loadSignoffs]);

  const handleRetract = useCallback(async (sectionId, signoffType) => {
    setSignoffErr('');
    try {
      await axios.delete(`${API_URL}/admin/rpa/docs/${docId}/workpapers/${sectionId}/signoff/${signoffType}`, { withCredentials: true });
      await loadSignoffs();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Retraction failed.';
      setSignoffErr(msg); throw e;
    }
  }, [docId, loadSignoffs]);

  // ── Save signature ───────────────────────────────────────────────────────
  const handleSaveSignature = useCallback(async (data) => {
    try {
      await axios.post(`${API_URL}/admin/profile/signature`, { data, mediaType: 'image/png' }, { withCredentials: true });
      setMySignature(data);
      setShowSigPad(false);
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save signature');
    }
  }, []);

  // ── Derived state ────────────────────────────────────────────────────────
  const summary       = wpData?.completion_summary || {};
  const header        = wpData?.package_header     || {};
  const priorityActions = wpData?.priority_actions_for_auditor || [];
  const sections      = wpData?.workpaper_sections  || [];
  const signoffs      = signoffData?.signoffs        || {};
  const sectionAuth   = signoffData?.sectionAuthority|| {};
  const userRole      = signoffData?.userRole;
  const signoffStats  = signoffData?.stats;

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        {/* ── Header ── */}
        <div style={s.header}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: 17, color: '#111827' }}>📋 Audit Workpaper Package</h2>
              {header.entity && <span style={{ fontSize: 12, color: '#6b7280' }}>{header.entity} — {header.period || 'Dec 31, 2024'}</span>}
              {userRole && <span style={{ ...s.badge('blue'), marginLeft: 4 }}>{AUDIT_ROLE_LABELS[userRole] || userRole}</span>}
            </div>
            <div style={{ display: 'flex', marginTop: 10, gap: 0 }}>
              <button style={s.tab(tab === 'workpapers')} onClick={() => setTab('workpapers')}>
                📋 Workpapers {signoffStats ? `(${signoffStats.fullySignedSections}/${signoffStats.totalSections} signed)` : ''}
              </button>
              <button style={s.tab(tab === 'issues')} onClick={() => setTab('issues')}>⚠️ Issues</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 10 }}>
            {/* My Signature button */}
            <button style={s.sigBtn(!!mySignature)} onClick={() => setShowSigPad(true)} title={mySignature ? 'View / update your signature' : 'Set up your signature'}>
              ✍ {mySignature ? 'My Signature ✓' : 'Set Signature'}
            </button>
            <button style={s.refreshBtn} onClick={refresh} disabled={loading}>{loading ? '…' : '↻'}</button>
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={s.body}>
          {signoffErr && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#991b1b', display: 'flex', justifyContent: 'space-between' }}>
              <span>⚠️ {signoffErr}</span>
              <button onClick={() => setSignoffErr('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
            </div>
          )}

          {/* ── WORKPAPERS TAB ── */}
          {tab === 'workpapers' && (
            <>
              {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}><div style={{ fontSize: 22 }}>⚙️</div><div style={{ marginTop: 6 }}>Generating workpaper package…</div></div>}
              {!loading && error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 16, color: '#991b1b' }}>
                  <strong>Error:</strong> {error}
                  <div style={{ marginTop: 8 }}><button onClick={refresh} style={s.actionBtn('#991b1b')}>Retry</button></div>
                </div>
              )}
              {!loading && !error && wpData && (
                <>
                  <div style={s.summaryGrid}>
                    {[
                      { label: 'Sections',        value: summary.total_sections ?? sections.length, color: '#111827' },
                      { label: 'Complete',         value: summary.complete ?? '—',                   color: '#059669' },
                      { label: 'Exceptions',       value: summary.has_exceptions ?? '—',             color: summary.has_exceptions > 0 ? '#dc2626' : '#059669' },
                      { label: 'Incomplete',       value: summary.incomplete ?? '—',                 color: '#f59e0b' },
                      { label: 'Total Exceptions', value: summary.total_exceptions_identified ?? '—', color: summary.total_exceptions_identified > 0 ? '#dc2626' : '#059669' },
                      { label: 'Sign-off %',       value: signoffStats ? `${signoffStats.completionPercentage}%` : '—', color: signoffStats?.fullySignedOff ? '#059669' : '#d97706' },
                      { label: 'Sections Signed',  value: signoffStats ? `${signoffStats.fullySignedSections}/${signoffStats.totalSections}` : '—', color: '#3b82f6' },
                      { label: 'High Priority',    value: summary.high_priority_actions ?? '—',       color: summary.high_priority_actions > 0 ? '#dc2626' : '#059669' },
                    ].map(tile => (
                      <div key={tile.label} style={s.summaryTile}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: tile.color }}>{tile.value}</div>
                        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{tile.label}</div>
                      </div>
                    ))}
                  </div>
                  {priorityActions.length > 0 && (
                    <div style={s.priorityBanner}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>🚨 High-Priority Auditor Actions ({priorityActions.length})</div>
                      {priorityActions.map((action, i) => (
                        <div key={i} style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 6, padding: 10, marginBottom: 7 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#991b1b' }}>{action.issue}</span>
                            <span style={{ fontSize: 10, color: '#6b7280' }}>{action.section_id}</span>
                          </div>
                          <div style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}><strong>Area:</strong> {action.account_or_area} &nbsp;|&nbsp; <strong>Section:</strong> {action.section}</div>
                          <div style={{ fontSize: 11, color: '#374151', marginBottom: 5 }}>{action.detail}</div>
                          {action.amount && action.amount !== '$0' && <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 5 }}><strong>Amount:</strong> {action.amount}</div>}
                          <div style={{ fontSize: 11, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: 6 }}><strong>Action Required:</strong> {action.action_required}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {header.purpose && <div style={{ fontSize: 11, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 12, lineHeight: 1.6 }}>{header.purpose}</div>}
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Workpaper Sections ({sections.length})</div>
                  {sections.map((section, i) => (
                    <WorkpaperSection
                      key={section.section_id || i}
                      section={section}
                      sectionSignoffs={signoffs[section.section_id] || {}}
                      sectionAuthority={sectionAuth[section.section_id] || null}
                      onSignoff={handleSignoff}
                      onRetract={handleRetract}
                      checkboxStates={checkboxStates}
                      onCheckboxToggle={handleCheckboxToggle}
                      userSignature={mySignature}
                      currentAdminId={currentAdminId}
                    />
                  ))}
                  <div style={{ marginTop: 18, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, color: '#6b7280' }}>
                    Generated by RPA Automated Audit System (Azure Government) · {header.generated_at ? new Date(header.generated_at).toLocaleString() : ''}
                    {signoffStats && ` · Sign-off: ${signoffStats.fullySignedSections}/${signoffStats.totalSections} sections`}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── ISSUES TAB ── */}
          {tab === 'issues' && <IssuesTab docId={docId} userRole={userRole} />}
        </div>
      </div>

      {/* ── SIGNATURE PAD MODAL ── */}
      {showSigPad && (
        <SignaturePad
          currentSignature={mySignature}
          onSave={handleSaveSignature}
          onClose={() => setShowSigPad(false)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTED BUTTON — props unchanged from original
// ─────────────────────────────────────────────────────────────────────────────
const GenerateWorkpaperButton = ({ docId, onDone }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    if (onDone) onDone();
  };

  return (
    <>
      <button style={s.triggerBtn} onClick={handleOpen} title="View Audit Workpapers & Issues">
        📋 Workpapers
      </button>
      {open && <WorkpaperModal docId={docId} onClose={() => setOpen(false)} />}
    </>
  );
};

export default GenerateWorkpaperButton;






// // src/components/RPA/buttons/GenerateWorkpaperButton.js
// // Workpaper button + modal with two tabs:
// //   📋 Workpapers — all sections with sign-off chain per section (Diligent-style)
// //   ⚠️  Issues     — raise, track, comment on, close audit issues
// //
// // Drop-in replacement. Props unchanged: docId, onDone.
// //
// // CHANGES IN THIS VERSION:
// //   - Multi-user checkbox support: each checkbox tracks an array of checkers.
// //     Each user sees their own checkbox state. Others' checks shown as read-only
// //     attribution beneath the checkbox label.
// //   - Users can only uncheck checkboxes they personally checked.
// //   - Unchecking any box in a section reloads sign-offs (backend resets the chain).
// //   - checkedCount / allChecked / mostRecentChecker now derived from current user's
// //     checks only, not the global state.

// import React, { useState, useCallback, useEffect, useRef } from 'react';
// import axios from 'axios';

// const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// // ─────────────────────────────────────────────────────────────────────────────
// // FRONTEND ROLE/SIGNOFF CONSTANTS (mirrors constants/auditRoles.js)
// // ─────────────────────────────────────────────────────────────────────────────
// const AUDIT_ROLE_LABELS = {
//   partner:            'Engagement Partner',
//   audit_manager:      'Audit Manager',
//   senior_auditor:     'Senior Auditor',
//   auditor:            'Auditor',
//   reviewer:           'Reviewer',
//   oversight_reviewer: 'Oversight Reviewer',
// };

// const SIGNOFF_TYPE_LABELS = {
//   preparer:         'Preparer',
//   detail_reviewer:  'Detail Reviewer',
//   general_reviewer: 'General Reviewer',
//   partner:          'Engagement Partner',
// };

// const ISSUE_TYPE_LABELS = {
//   material_weakness:       'Material Weakness',
//   significant_deficiency:  'Significant Deficiency',
//   finding:                 'Finding',
//   observation:             'Observation',
//   control_deficiency:      'Control Deficiency',
//   compliance_violation:    'Compliance Violation',
//   other:                   'Other',
// };

// const SEVERITY_COLORS = {
//   critical: '#991b1b',
//   high:     '#b45309',
//   moderate: '#1e40af',
//   low:      '#374151',
// };

// const STATUS_COLORS = {
//   open:           '#dc2626',
//   in_remediation: '#d97706',
//   pending_review: '#2563eb',
//   closed:         '#059669',
//   escalated:      '#7c3aed',
//   voided:         '#9ca3af',
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // STYLE TOKENS
// // ─────────────────────────────────────────────────────────────────────────────
// const s = {
//   overlay: {
//     position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
//     zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
//     overflowY: 'auto', padding: '24px 16px',
//   },
//   modal: {
//     background: '#fff', borderRadius: 12, width: '100%', maxWidth: 940,
//     boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
//   },
//   header: {
//     display: 'flex', justifyContent: 'space-between', alignItems: 'center',
//     padding: '18px 24px 0', borderBottom: '1px solid #e5e7eb',
//     position: 'sticky', top: 0, background: '#fff', zIndex: 10, borderRadius: '12px 12px 0 0',
//   },
//   body: { padding: '16px 24px 32px' },
//   closeBtn: {
//     background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
//     padding: '5px 12px', cursor: 'pointer', fontSize: 13, color: '#374151',
//   },
//   triggerBtn: {
//     background: '#7c3aed', color: '#fff', border: 'none',
//     padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
//     display: 'flex', alignItems: 'center', gap: 4,
//   },
//   refreshBtn: {
//     background: 'none', border: '1px solid #7c3aed', color: '#7c3aed',
//     padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
//   },
//   tab: (active) => ({
//     padding: '8px 16px', marginRight: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500,
//     borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
//     color: active ? '#7c3aed' : '#6b7280', background: 'none', border: 'none',
//   }),
//   sectionCard: {
//     border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
//   },
//   sectionHeader: {
//     display: 'flex', justifyContent: 'space-between', alignItems: 'center',
//     padding: '9px 14px', cursor: 'pointer', userSelect: 'none', background: '#f9fafb',
//   },
//   sectionBody: { padding: '12px 16px', borderTop: '1px solid #e5e7eb' },
//   badge: (color) => ({
//     display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 600,
//     background: color === 'green' ? '#d1fae5' : color === 'red' ? '#fee2e2' : color === 'yellow' ? '#fef3c7' : color === 'blue' ? '#dbeafe' : color === 'purple' ? '#ede9fe' : '#f3f4f6',
//     border: `1px solid ${color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'yellow' ? '#f59e0b' : color === 'blue' ? '#3b82f6' : color === 'purple' ? '#8b5cf6' : '#d1d5db'}`,
//     color: color === 'green' ? '#065f46' : color === 'red' ? '#991b1b' : color === 'yellow' ? '#92400e' : color === 'blue' ? '#1e40af' : color === 'purple' ? '#5b21b6' : '#374151',
//   }),
//   exceptionCard: {
//     background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: 10, marginBottom: 8,
//   },
//   checkpointCard: {
//     background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: 12,
//   },
//   dataRow: { display: 'flex', gap: 6, marginBottom: 4, fontSize: 12 },
//   label: { color: '#6b7280', minWidth: 160 },
//   value: { color: '#111827', fontWeight: 500 },
//   priorityBanner: {
//     background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 14,
//   },
//   summaryGrid: {
//     display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 14,
//   },
//   summaryTile: {
//     border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', textAlign: 'center',
//   },
//   signoffPanel: {
//     marginTop: 14, borderTop: '2px solid #e5e7eb', paddingTop: 12,
//   },
//   signoffRow: {
//     display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6,
//     padding: '7px 10px', borderRadius: 6,
//   },
//   signoffBtn: {
//     background: '#7c3aed', color: '#fff', border: 'none',
//     padding: '3px 10px', borderRadius: 4, fontSize: 10, cursor: 'pointer', fontWeight: 600,
//     flexShrink: 0,
//   },
//   retractBtn: {
//     background: 'none', border: '1px solid #d1d5db', color: '#6b7280',
//     padding: '3px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer', flexShrink: 0,
//   },
//   notesInput: {
//     width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
//     fontSize: 11, marginTop: 5, resize: 'vertical', minHeight: 38, boxSizing: 'border-box',
//     fontFamily: 'inherit',
//   },
//   issueCard: {
//     border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
//   },
//   issueHeader: {
//     display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
//     padding: '10px 14px', cursor: 'pointer', background: '#f9fafb',
//   },
//   issueBody: { padding: '12px 16px', borderTop: '1px solid #e5e7eb' },
//   textarea: {
//     width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px',
//     fontSize: 12, resize: 'vertical', minHeight: 70, boxSizing: 'border-box',
//     fontFamily: 'inherit', lineHeight: 1.5,
//   },
//   input: {
//     width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
//     fontSize: 12, boxSizing: 'border-box', fontFamily: 'inherit',
//   },
//   select: {
//     border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
//     fontSize: 12, background: '#fff', cursor: 'pointer',
//   },
//   actionBtn: (color) => ({
//     background: color || '#1d4ed8', color: '#fff', border: 'none',
//     padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 500,
//   }),
//   ghostBtn: {
//     background: 'none', border: '1px solid #d1d5db', color: '#374151',
//     padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
//   },
//   sigBtn: (hasSig) => ({
//     background: hasSig ? '#f0fdf4' : '#fef3c7',
//     border: `1px solid ${hasSig ? '#22c55e' : '#f59e0b'}`,
//     color: hasSig ? '#065f46' : '#92400e',
//     padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500,
//     display: 'flex', alignItems: 'center', gap: 4,
//   }),
//   commentBubble: {
//     background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6,
//     padding: '8px 12px', marginBottom: 6,
//   },
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // HELPERS
// // ─────────────────────────────────────────────────────────────────────────────
// const statusColor = (s) => s === 'complete' ? 'green' : s === 'exception' ? 'red' : s === 'incomplete' ? 'yellow' : 'grey';
// const statusLabel = (s) => s === 'complete' ? '✅ Complete' : s === 'exception' ? '⚠️ Exception' : s === 'incomplete' ? '⏸ Incomplete' : s === 'error' ? '❌ Error' : s || '—';
// const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
// const fmtShort = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// // Build a stable checkbox ID from section + field index
// const cbId = (sectionId, idx) => `${sectionId}__cb${idx}`;

// // ─────────────────────────────────────────────────────────────────────────────
// // SIGNATURE PAD — canvas-based drawing component
// // ─────────────────────────────────────────────────────────────────────────────
// const SignaturePad = ({ currentSignature, onSave, onClose }) => {
//   const canvasRef  = useRef(null);
//   const isDrawing  = useRef(false);
//   const lastPos    = useRef({ x: 0, y: 0 });
//   const [isEmpty,  setIsEmpty]  = useState(!currentSignature);
//   const [showExisting, setShowExisting] = useState(!!currentSignature);

//   const getPos = (e, canvas) => {
//     const rect = canvas.getBoundingClientRect();
//     const src  = e.touches?.[0] || e;
//     return {
//       x: (src.clientX - rect.left) * (canvas.width  / rect.width),
//       y: (src.clientY - rect.top)  * (canvas.height / rect.height),
//     };
//   };

//   const startDraw = (e) => {
//     e.preventDefault();
//     setShowExisting(false);
//     setIsEmpty(false);
//     const canvas = canvasRef.current;
//     isDrawing.current = true;
//     lastPos.current   = getPos(e, canvas);
//   };

//   const draw = (e) => {
//     if (!isDrawing.current) return;
//     e.preventDefault();
//     const canvas = canvasRef.current;
//     const ctx    = canvas.getContext('2d');
//     const pos    = getPos(e, canvas);
//     ctx.beginPath();
//     ctx.moveTo(lastPos.current.x, lastPos.current.y);
//     ctx.lineTo(pos.x, pos.y);
//     ctx.strokeStyle = '#1e40af';
//     ctx.lineWidth   = 2.5;
//     ctx.lineCap     = 'round';
//     ctx.lineJoin    = 'round';
//     ctx.stroke();
//     lastPos.current = pos;
//   };

//   const endDraw = () => { isDrawing.current = false; };

//   const clear = () => {
//     const canvas = canvasRef.current;
//     canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
//     setIsEmpty(true);
//     setShowExisting(false);
//   };

//   const save = () => {
//     const canvas = canvasRef.current;
//     if (isEmpty && !showExisting) { alert('Please draw your signature before saving.'); return; }
//     const data = showExisting ? currentSignature : canvas.toDataURL('image/png');
//     onSave(data);
//   };

//   return (
//     <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//       <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
//         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
//           <div>
//             <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>✍ My Signature</h3>
//             <p style={{ margin: '3px 0 0', fontSize: 11, color: '#6b7280' }}>Draw in the box below. Used on workpaper checkpoints and sign-offs.</p>
//           </div>
//           <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
//         </div>

//         {currentSignature && showExisting && (
//           <div style={{ border: '2px solid #86efac', borderRadius: 8, background: '#f0fdf4', padding: 12, marginBottom: 12, textAlign: 'center' }}>
//             <div style={{ fontSize: 11, color: '#065f46', marginBottom: 6 }}>Current saved signature:</div>
//             <img src={currentSignature} alt="Saved signature" style={{ maxHeight: 80, maxWidth: '100%' }} />
//             <div style={{ marginTop: 8 }}>
//               <button onClick={() => { setShowExisting(false); setIsEmpty(true); const canvas = canvasRef.current; canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height); }} style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
//                 Replace with new drawing
//               </button>
//             </div>
//           </div>
//         )}

//         <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, background: '#f9fafb', position: 'relative', display: showExisting ? 'none' : 'block' }}>
//           <canvas
//             ref={canvasRef}
//             width={440}
//             height={160}
//             style={{ display: 'block', cursor: 'crosshair', touchAction: 'none', width: '100%', borderRadius: 6 }}
//             onMouseDown={startDraw}
//             onMouseMove={draw}
//             onMouseUp={endDraw}
//             onMouseLeave={endDraw}
//             onTouchStart={startDraw}
//             onTouchMove={draw}
//             onTouchEnd={endDraw}
//           />
//           {isEmpty && (
//             <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
//               <span style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>Draw your signature here</span>
//             </div>
//           )}
//         </div>

//         <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'space-between', alignItems: 'center' }}>
//           <button onClick={clear} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#374151' }}>
//             Clear
//           </button>
//           <div style={{ display: 'flex', gap: 8 }}>
//             <button onClick={onClose} style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: '#fff', color: '#374151' }}>Cancel</button>
//             <button onClick={save} style={{ padding: '7px 18px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
//               Save Signature
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // SIGN-OFF PANEL — per workpaper section
// // ─────────────────────────────────────────────────────────────────────────────
// const SignoffPanel = ({ sectionId, sectionSignoffs = {}, sectionAuthority, onSignoff, onRetract }) => {
//   const [notesOpen, setNotesOpen] = useState(null);
//   const [notes, setNotes]         = useState('');
//   const [busy, setBusy]           = useState(false);

//   const chain          = sectionAuthority?.chain || [];
//   const nextPending    = sectionAuthority?.nextPending;
//   const canSign        = sectionAuthority?.canSign;
//   const cantReason     = sectionAuthority?.cannotSignReason;
//   const retractable    = sectionAuthority?.retractable || {};
//   const fullySignedOff = sectionAuthority?.fullySignedOff;

//   if (!chain.length) return null;

//   const handleSign = async (signoffType) => {
//     setBusy(true);
//     try { await onSignoff(sectionId, signoffType, notes); setNotesOpen(null); setNotes(''); }
//     finally { setBusy(false); }
//   };

//   const handleRetract = async (signoffType) => {
//     if (!window.confirm(`Retract your ${SIGNOFF_TYPE_LABELS[signoffType]} sign-off?`)) return;
//     setBusy(true);
//     try { await onRetract(sectionId, signoffType); }
//     finally { setBusy(false); }
//   };

//   return (
//     <div style={s.signoffPanel}>
//       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
//         <span style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>🖊 Sign-Off Chain</span>
//         {fullySignedOff ? <span style={s.badge('green')}>✅ Fully Signed Off</span> : <span style={s.badge('yellow')}>⏳ Pending</span>}
//       </div>

//       {chain.map((type) => {
//         const existing   = sectionSignoffs[type];
//         const isSigned   = !!existing?.signedAt;
//         const isNext     = type === nextPending;
//         const isMyTurn   = isNext && canSign;
//         const isLocked   = !isSigned && !isNext;
//         const canRetract = retractable[type] === true;

//         let bg = '#f9fafb', icon = '🔒';
//         if (isSigned)                    { bg = '#f0fdf4'; icon = '✅'; }
//         else if (isMyTurn)               { bg = '#eff6ff'; icon = '⏳'; }
//         else if (isNext && !canSign)     { bg = '#fafafa'; icon = '🚫'; }

//         return (
//           <div key={type} style={{ ...s.signoffRow, background: bg }}>
//             <span style={{ fontSize: 15, width: 20, flexShrink: 0, marginTop: 1 }}>{icon}</span>
//             <div style={{ flex: 1, minWidth: 0 }}>
//               <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{SIGNOFF_TYPE_LABELS[type]}</div>
//               {isSigned ? (
//                 <>
//                   <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
//                     {existing.name}
//                     {existing.auditRole ? ` · ${AUDIT_ROLE_LABELS[existing.auditRole] || existing.auditRole}` : ''}
//                     {' · '}{fmtDate(existing.signedAt)}
//                   </div>
//                   {existing.notes && <div style={{ fontSize: 10, color: '#4b5563', fontStyle: 'italic', marginTop: 1 }}>"{existing.notes}"</div>}
//                   {existing.signature && (
//                     <div style={{ marginTop: 4 }}>
//                       <img
//                         src={existing.signature}
//                         alt={`${existing.name} signature`}
//                         style={{ maxHeight: 36, maxWidth: 160, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', padding: '2px 4px' }}
//                       />
//                     </div>
//                   )}
//                 </>
//               ) : isLocked ? (
//                 <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>Waiting on prior sign-off</div>
//               ) : isNext && !canSign ? (
//                 <div style={{ fontSize: 10, color: '#b45309', marginTop: 1 }}>{cantReason}</div>
//               ) : isMyTurn ? (
//                 <>
//                   <div style={{ fontSize: 10, color: '#1d4ed8', marginTop: 1 }}>Your signature required</div>
//                   {notesOpen === type && (
//                     <textarea style={s.notesInput} placeholder="Optional reviewer notes…" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
//                   )}
//                 </>
//               ) : null}
//             </div>
//             <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
//               {isSigned && canRetract && (
//                 <button style={s.retractBtn} onClick={() => handleRetract(type)} disabled={busy}>Retract</button>
//               )}
//               {isMyTurn && (
//                 <>
//                   <button style={{ ...s.retractBtn, color: '#374151' }} onClick={() => { setNotesOpen(notesOpen === type ? null : type); if (notesOpen === type) setNotes(''); }} disabled={busy}>
//                     {notesOpen === type ? 'Cancel' : '📝'}
//                   </button>
//                   <button style={s.signoffBtn} onClick={() => handleSign(type)} disabled={busy}>
//                     {busy ? '…' : 'Sign Off ▸'}
//                   </button>
//                 </>
//               )}
//             </div>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // WORKPAPER SECTION — collapsible card with sign-off panel
// // ─────────────────────────────────────────────────────────────────────────────
// const WorkpaperSection = ({
//   section, sectionSignoffs, sectionAuthority, onSignoff, onRetract,
//   checkboxStates, onCheckboxToggle, userSignature, currentAdminId,
// }) => {
//   const [open, setOpen] = useState(false);
//   if (!section) return null;

//   const color           = statusColor(section.status);
//   const hasExceptions   = section.exceptions?.length > 0;
//   const hasCheckpoint   = !!section.verification_checkpoint;
//   const hasResults      = section.results && Object.keys(section.results).length > 0;
//   const isFullySigned   = sectionAuthority?.fullySignedOff;
//   const nextPending     = sectionAuthority?.nextPending;
//   const sId             = section.section_id;

//   // ── Multi-user checkpoint completion state ──────────────────────────────
//   // checkedCount = number of checkboxes that the CURRENT USER has checked.
//   // Other users' checks are shown as read-only attribution beneath each label.
//   const checkpointFields = section.verification_checkpoint?.fields || [];

//   const checkedCount = checkpointFields.filter((_, i) => {
//     const state = checkboxStates?.[cbId(sId, i)];
//     return (state?.checks || []).some(c => c.adminId === currentAdminId);
//   }).length;

//   const allChecked = checkpointFields.length > 0 && checkedCount === checkpointFields.length;

//   // Most recent check by the current user across all checkboxes in this section
//   const myRecentChecks = checkpointFields
//     .map((_, i) => (checkboxStates?.[cbId(sId, i)]?.checks || []).find(c => c.adminId === currentAdminId))
//     .filter(Boolean)
//     .sort((a, b) => new Date(b.checkedAt) - new Date(a.checkedAt));
//   const mostRecentChecker = myRecentChecks[0] || null;
//   const currentUserCheckedAny = myRecentChecks.length > 0;

//   const renderValue = (val) => {
//     if (val === null || val === undefined) return '—';
//     if (typeof val === 'boolean') return val ? 'Yes' : 'No';
//     if (Array.isArray(val)) {
//       if (!val.length) return '—';
//       if (typeof val[0] === 'string') return val.join(', ');
//       return (
//         <div style={{ marginTop: 4 }}>
//           {val.map((item, i) => (
//             <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 6, marginBottom: 4, fontSize: 11 }}>
//               {typeof item === 'object'
//                 ? Object.entries(item).map(([k, v]) => (
//                     <div key={k} style={s.dataRow}>
//                       <span style={s.label}>{k.replace(/_/g, ' ')}:</span>
//                       <span style={s.value}>{String(v ?? '—')}</span>
//                     </div>
//                   ))
//                 : String(item)}
//             </div>
//           ))}
//         </div>
//       );
//     }
//     if (typeof val === 'object') {
//       return (
//         <div style={{ marginTop: 4 }}>
//           {Object.entries(val).map(([k, v]) => (
//             <div key={k} style={s.dataRow}>
//               <span style={s.label}>{k.replace(/_/g, ' ')}:</span>
//               <span style={{ ...s.value, flex: 1 }}>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}</span>
//             </div>
//           ))}
//         </div>
//       );
//     }
//     return String(val);
//   };

//   return (
//     <div style={s.sectionCard}>
//       <div style={s.sectionHeader} onClick={() => setOpen(!open)}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//           <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
//             {section.section_id} — {section.title}
//           </span>
//           {section.reference && <span style={{ fontSize: 10, color: '#6b7280', fontStyle: 'italic' }}>{section.reference}</span>}
//         </div>
//         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
//           {isFullySigned
//             ? <span style={s.badge('green')}>✅ Signed</span>
//             : nextPending
//               ? <span style={s.badge('blue')}>🖊 {SIGNOFF_TYPE_LABELS[nextPending]}</span>
//               : null}
//           {hasCheckpoint && (
//             <span style={s.badge(allChecked ? 'green' : 'grey')}>
//               {checkedCount}/{checkpointFields.length} ✓
//             </span>
//           )}
//           {hasExceptions && <span style={s.badge('red')}>{section.exceptions.length} exception{section.exceptions.length !== 1 ? 's' : ''}</span>}
//           <span style={s.badge(color)}>{statusLabel(section.status)}</span>
//           <span style={{ fontSize: 13, color: '#9ca3af' }}>{open ? '▲' : '▼'}</span>
//         </div>
//       </div>

//       {open && (
//         <div style={s.sectionBody}>
//           {(section.status === 'incomplete' || section.status === 'error') && section.reason && (
//             <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12, color: '#92400e' }}>
//               ℹ️ {section.reason}
//             </div>
//           )}
//           {section.purpose && (
//             <div style={{ marginBottom: 12 }}>
//               <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Purpose</div>
//               <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6, background: '#f9fafb', padding: 8, borderRadius: 4 }}>{section.purpose}</div>
//             </div>
//           )}
//           {section.procedure_performed && (
//             <div style={{ marginBottom: 12 }}>
//               <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Procedures Performed</div>
//               <pre style={{ fontSize: 11, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: 10, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.6 }}>
//                 {section.procedure_performed}
//               </pre>
//             </div>
//           )}
//           {section.findings_narrative && (
//             <div style={{ marginBottom: 12 }}>
//               <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Auditor's Opinion</div>
//               <div style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: 10, lineHeight: 1.7 }}>{section.findings_narrative}</div>
//             </div>
//           )}
//           {hasResults && (
//             <div style={{ marginBottom: 12 }}>
//               <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Results</div>
//               <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
//                 {Object.entries(section.results).map(([key, val]) => (
//                   <div key={key} style={s.dataRow}>
//                     <span style={s.label}>{key.replace(/_/g, ' ')}:</span>
//                     <span style={{ ...s.value, flex: 1 }}>{renderValue(val)}</span>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}
//           {hasExceptions && (
//             <div style={{ marginBottom: 12 }}>
//               <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>⚠️ Exceptions ({section.exceptions.length})</div>
//               {section.exceptions.map((ex, i) => (
//                 <div key={i} style={s.exceptionCard}>
//                   <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
//                     <span style={{ fontSize: 11, fontWeight: 600, color: '#991b1b' }}>{ex.type}</span>
//                     <span style={s.badge(ex.severity === 'HIGH' || ex.severity === 'MATERIAL' ? 'red' : 'yellow')}>{ex.severity}</span>
//                   </div>
//                   {(ex.account || ex.customer || ex.warehouse || ex.bank) && (
//                     <div style={{ fontSize: 11, color: '#374151', marginBottom: 3 }}>
//                       <strong>{ex.account || ex.customer || ex.warehouse || ex.bank}</strong>
//                     </div>
//                   )}
//                   <div style={{ fontSize: 11, color: '#374151', marginBottom: 5, lineHeight: 1.5 }}>{ex.detail}</div>
//                   {ex.amount && ex.amount !== '$0' && (
//                     <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 5 }}><strong>Amount:</strong> {ex.amount}</div>
//                   )}
//                   <div style={{ fontSize: 11, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: 6, lineHeight: 1.5 }}>
//                     <strong>Action Required:</strong> {ex.action_required}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           )}

//           {/* ── VERIFICATION CHECKPOINT — multi-user checkboxes ── */}
//           {hasCheckpoint && (
//             <div style={s.checkpointCard}>
//               <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
//                 ✋ Auditor Verification Checkpoint
//                 {checkpointFields.length > 0 && (
//                   <span style={{ ...s.badge(allChecked ? 'green' : 'blue'), marginLeft: 8 }}>
//                     {checkedCount}/{checkpointFields.length} my checks
//                   </span>
//                 )}
//               </div>
//               <div style={{ fontSize: 12, color: '#1e3a8a', marginBottom: 8, lineHeight: 1.6 }}>
//                 {section.verification_checkpoint.instruction}
//               </div>
//               <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
//                 {checkpointFields.map((field, i) => {
//                   const id         = cbId(sId, i);
//                   const state      = checkboxStates?.[id];
//                   const checks     = state?.checks || [];

//                   // Current user's check state
//                   const isCheckedByMe = checks.some(c => c.adminId === currentAdminId);
//                   const myCheck       = checks.find(c => c.adminId === currentAdminId);

//                   // Other auditors who have also checked this box
//                   const otherChecks = checks.filter(c => c.adminId !== currentAdminId);

//                   return (
//                     <div key={i} style={{ paddingBottom: otherChecks.length > 0 ? 6 : 0 }}>
//                       <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
//                         <input
//                           type="checkbox"
//                           checked={isCheckedByMe}
//                           onChange={(e) => onCheckboxToggle && onCheckboxToggle(id, sId, field, e.target.checked)}
//                           style={{ width: 16, height: 16, marginTop: 1, cursor: 'pointer', accentColor: '#3b82f6', flexShrink: 0 }}
//                         />
//                         <div style={{ flex: 1 }}>
//                           <span style={{ fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>{field}</span>
//                           {/* Current user's check attribution */}
//                           {isCheckedByMe && myCheck && (
//                             <div style={{ fontSize: 10, color: '#059669', marginTop: 2 }}>
//                               ✓ You · {fmtShort(myCheck.checkedAt)}
//                             </div>
//                           )}
//                           {/* Other auditors who have checked this box (read-only display) */}
//                           {otherChecks.length > 0 && (
//                             <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '0 12px' }}>
//                               {otherChecks.map(c => (
//                                 <span key={c.adminId}>✓ {c.adminName} · {fmtShort(c.checkedAt)}</span>
//                               ))}
//                             </div>
//                           )}
//                         </div>
//                       </label>
//                     </div>
//                   );
//                 })}
//               </div>

//               {/* ── Signature line — shows current user's signature if they checked any box ── */}
//               <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #bfdbfe', display: 'flex', alignItems: 'center', gap: 16 }}>
//                 <div style={{ flex: 1 }}>
//                   {currentUserCheckedAny && userSignature ? (
//                     <div>
//                       <img
//                         src={userSignature}
//                         alt="Auditor signature"
//                         style={{ maxHeight: 44, maxWidth: 180, border: '1px solid #bfdbfe', borderRadius: 4, background: '#fff', padding: '2px 6px' }}
//                       />
//                     </div>
//                   ) : (
//                     <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
//                       Signature: _________________________
//                     </span>
//                   )}
//                 </div>
//                 <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
//                   Date: {mostRecentChecker?.checkedAt ? fmtShort(mostRecentChecker.checkedAt) : '_____________'}
//                 </div>
//                 {!userSignature && checkedCount > 0 && (
//                   <div style={{ fontSize: 10, color: '#b45309', fontStyle: 'italic' }}>
//                     Set your signature to display here
//                   </div>
//                 )}
//               </div>
//             </div>
//           )}

//           {section.completed_at && (
//             <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>Completed: {section.completed_at}</div>
//           )}

//           {/* ── SIGN-OFF PANEL ── */}
//           {sectionAuthority && (
//             <SignoffPanel
//               sectionId={section.section_id}
//               sectionSignoffs={sectionSignoffs || {}}
//               sectionAuthority={sectionAuthority}
//               onSignoff={onSignoff}
//               onRetract={onRetract}
//             />
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // ISSUE DETAIL PANEL (expanded view inside issues tab)
// // ─────────────────────────────────────────────────────────────────────────────
// const IssueDetail = ({ issue, docId, userRole, onRefresh }) => {
//   const [mgmtText,       setMgmtText]       = useState(issue.managementResponse?.text || '');
//   const [remPlan,        setRemPlan]         = useState(issue.remediationPlan?.text || '');
//   const [remDate,        setRemDate]         = useState(issue.remediationPlan?.targetDate ? issue.remediationPlan.targetDate.slice(0, 10) : '');
//   const [commentText,    setCommentText]     = useState('');
//   const [commentInternal,setCommentInternal] = useState(false);
//   const [elevSeverity,   setElevSeverity]    = useState('high');
//   const [closureNotes,   setClosureNotes]    = useState('');
//   const [busy,           setBusy]            = useState('');

//   const api = (method, path, data) =>
//     axios[method](`${API_URL}/admin/rpa/docs/${docId}/issues/${issue._id}${path}`, data, { withCredentials: true });

//   const save = async (label, fn) => {
//     setBusy(label);
//     try { await fn(); await onRefresh(); } catch (e) { alert(e?.response?.data?.error || e.message); }
//     finally { setBusy(''); }
//   };

//   const visibleComments = (issue.comments || []).filter(c => !c.deletedAt);

//   return (
//     <div>
//       {/* Management Response */}
//       <div style={{ marginBottom: 14 }}>
//         <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Management Response</div>
//         <textarea
//           style={s.textarea}
//           placeholder="Enter management's written response to this finding…"
//           value={mgmtText}
//           onChange={e => setMgmtText(e.target.value)}
//           rows={3}
//         />
//         {issue.managementResponse?.respondedBy && (
//           <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3 }}>
//             Last updated: {issue.managementResponse.respondedBy?.name} · {fmtDate(issue.managementResponse.lastUpdatedAt || issue.managementResponse.respondedAt)}
//           </div>
//         )}
//         <button
//           style={{ ...s.actionBtn('#1d4ed8'), marginTop: 6 }}
//           disabled={busy === 'mgmt'}
//           onClick={() => save('mgmt', () => api('patch', '/management-response', { text: mgmtText }))}
//         >
//           {busy === 'mgmt' ? 'Saving…' : 'Save Response'}
//         </button>
//       </div>

//       {/* Remediation Plan */}
//       <div style={{ marginBottom: 14 }}>
//         <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5 }}>Remediation Plan</div>
//         <textarea
//           style={s.textarea}
//           placeholder="Describe the corrective action plan and steps to be taken…"
//           value={remPlan}
//           onChange={e => setRemPlan(e.target.value)}
//           rows={3}
//         />
//         <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
//           <label style={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap' }}>Target Date:</label>
//           <input type="date" style={{ ...s.input, width: 160 }} value={remDate} onChange={e => setRemDate(e.target.value)} />
//           <button
//             style={s.actionBtn('#1d4ed8')}
//             disabled={busy === 'rem'}
//             onClick={() => save('rem', () => api('patch', '/remediation-plan', { text: remPlan, targetDate: remDate || null }))}
//           >
//             {busy === 'rem' ? 'Saving…' : 'Save Plan'}
//           </button>
//         </div>
//       </div>

//       {/* Comments */}
//       <div style={{ marginBottom: 14 }}>
//         <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
//           Comments ({visibleComments.length})
//         </div>
//         {visibleComments.length > 0 && (
//           <div style={{ marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
//             {visibleComments.map(c => (
//               <div key={c._id} style={s.commentBubble}>
//                 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
//                   <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>
//                     {c.author?.name || 'Unknown'}
//                     {c.isInternal && <span style={{ ...s.badge('purple'), marginLeft: 4 }}>Internal</span>}
//                   </span>
//                   <span style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(c.createdAt)}</span>
//                 </div>
//                 <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.5 }}>{c.text}</div>
//               </div>
//             ))}
//           </div>
//         )}
//         <textarea
//           style={s.textarea}
//           placeholder="Add a comment…"
//           value={commentText}
//           onChange={e => setCommentText(e.target.value)}
//           rows={2}
//         />
//         <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
//           <label style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
//             <input type="checkbox" checked={commentInternal} onChange={e => setCommentInternal(e.target.checked)} />
//             Internal only
//           </label>
//           <button
//             style={s.actionBtn('#374151')}
//             disabled={busy === 'comment' || !commentText.trim()}
//             onClick={() => save('comment', async () => {
//               await api('post', '/comments', { text: commentText, isInternal: commentInternal });
//               setCommentText('');
//             })}
//           >
//             {busy === 'comment' ? '…' : 'Add Comment'}
//           </button>
//         </div>
//       </div>

//       {/* Elevate + Close actions */}
//       <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
//         {issue.status !== 'closed' && (
//           <>
//             <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
//               <select style={s.select} value={elevSeverity} onChange={e => setElevSeverity(e.target.value)}>
//                 <option value="critical">Critical</option>
//                 <option value="high">High</option>
//                 <option value="moderate">Moderate</option>
//                 <option value="low">Low</option>
//               </select>
//               <button
//                 style={s.actionBtn('#7c3aed')}
//                 disabled={busy === 'elevate'}
//                 onClick={() => {
//                   const reason = window.prompt('Reason for elevation (optional):') || '';
//                   save('elevate', () => api('post', '/elevate', { toSeverity: elevSeverity, reason }));
//                 }}
//               >
//                 {busy === 'elevate' ? '…' : '↑ Elevate'}
//               </button>
//             </div>
//             <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
//               <input
//                 style={{ ...s.input, width: 180 }}
//                 placeholder="Closure notes…"
//                 value={closureNotes}
//                 onChange={e => setClosureNotes(e.target.value)}
//               />
//               <button
//                 style={s.actionBtn('#059669')}
//                 disabled={busy === 'close'}
//                 onClick={() => save('close', () => api('post', '/close', { closureNotes }))}
//               >
//                 {busy === 'close' ? '…' : '✓ Close Issue'}
//               </button>
//             </div>
//           </>
//         )}
//         {issue.status === 'closed' && (
//           <button
//             style={s.actionBtn('#d97706')}
//             disabled={busy === 'reopen'}
//             onClick={() => save('reopen', () => api('post', '/reopen', {}))}
//           >
//             {busy === 'reopen' ? '…' : '↩ Reopen'}
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // ISSUES TAB
// // ─────────────────────────────────────────────────────────────────────────────
// const IssuesTab = ({ docId, userRole }) => {
//   const [issues,       setIssues]       = useState([]);
//   const [stats,        setStats]        = useState({});
//   const [loading,      setLoading]      = useState(true);
//   const [expanded,     setExpanded]     = useState(null);
//   const [showForm,     setShowForm]     = useState(false);
//   const [filterStatus, setFilterStatus] = useState('');
//   const [form, setForm] = useState({ title: '', description: '', type: 'finding', severity: 'moderate', priority: 'medium', workpaperRef: '', amount: '' });
//   const [saving, setSaving] = useState(false);
//   const [err,    setErr]    = useState('');

//   const load = useCallback(async () => {
//     setLoading(true);
//     try {
//       const params = filterStatus ? `?status=${filterStatus}` : '';
//       const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/issues${params}`, { withCredentials: true });
//       setIssues(res.data?.issues || []);
//       setStats(res.data?.stats || {});
//     } catch (e) { setErr('Failed to load issues'); }
//     finally { setLoading(false); }
//   }, [docId, filterStatus]);

//   useEffect(() => { load(); }, [load]);

//   const createIssue = async () => {
//     if (!form.title.trim()) { setErr('Title is required'); return; }
//     setSaving(true); setErr('');
//     try {
//       await axios.post(`${API_URL}/admin/rpa/docs/${docId}/issues`, {
//         ...form,
//         amount: form.amount ? Number(form.amount) : null,
//         workpaperRef: form.workpaperRef || null,
//       }, { withCredentials: true });
//       setForm({ title: '', description: '', type: 'finding', severity: 'moderate', priority: 'medium', workpaperRef: '', amount: '' });
//       setShowForm(false);
//       await load();
//     } catch (e) { setErr(e?.response?.data?.error || 'Failed to create issue'); }
//     finally { setSaving(false); }
//   };

//   return (
//     <div>
//       {/* Stats bar */}
//       <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
//         {[
//           { label: 'Total',       value: stats.total          || 0, color: '#374151' },
//           { label: 'Open',        value: stats.open           || 0, color: '#dc2626' },
//           { label: 'Remediation', value: stats.in_remediation || 0, color: '#d97706' },
//           { label: 'Closed',      value: stats.closed         || 0, color: '#059669' },
//           { label: 'Critical',    value: stats.critical       || 0, color: '#7c3aed' },
//         ].map(t => (
//           <div key={t.label} style={{ ...s.summaryTile, minWidth: 70, padding: '6px 10px' }}>
//             <div style={{ fontSize: 18, fontWeight: 700, color: t.color }}>{t.value}</div>
//             <div style={{ fontSize: 10, color: '#6b7280' }}>{t.label}</div>
//           </div>
//         ))}
//         <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
//           <select style={{ ...s.select, fontSize: 11 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
//             <option value="">All statuses</option>
//             <option value="open">Open</option>
//             <option value="in_remediation">In Remediation</option>
//             <option value="closed">Closed</option>
//             <option value="escalated">Escalated</option>
//           </select>
//           <button style={s.actionBtn('#7c3aed')} onClick={() => setShowForm(!showForm)}>
//             {showForm ? 'Cancel' : '+ Raise Issue'}
//           </button>
//         </div>
//       </div>

//       {/* Raise issue form */}
//       {showForm && (
//         <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 14, background: '#f9fafb' }}>
//           <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 10 }}>Raise New Issue</div>
//           {err && <div style={{ color: '#991b1b', fontSize: 11, marginBottom: 8 }}>{err}</div>}
//           <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
//             <div style={{ gridColumn: '1/-1' }}>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Title *</label>
//               <input style={s.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Brief issue title…" />
//             </div>
//             <div>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Type</label>
//               <select style={{ ...s.select, width: '100%' }} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
//                 {Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
//               </select>
//             </div>
//             <div>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Severity</label>
//               <select style={{ ...s.select, width: '100%' }} value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
//                 <option value="critical">Critical</option>
//                 <option value="high">High</option>
//                 <option value="moderate">Moderate</option>
//                 <option value="low">Low</option>
//               </select>
//             </div>
//             <div>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Workpaper Ref</label>
//               <input style={s.input} value={form.workpaperRef} onChange={e => setForm(f => ({ ...f, workpaperRef: e.target.value }))} placeholder="e.g. WP-300" />
//             </div>
//             <div>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Dollar Amount</label>
//               <input style={s.input} type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
//             </div>
//             <div style={{ gridColumn: '1/-1' }}>
//               <label style={{ fontSize: 11, color: '#374151', display: 'block', marginBottom: 3 }}>Description</label>
//               <textarea style={s.textarea} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue, its source, and impact…" />
//             </div>
//           </div>
//           <div style={{ display: 'flex', gap: 8 }}>
//             <button style={s.actionBtn('#dc2626')} onClick={createIssue} disabled={saving}>{saving ? 'Raising…' : 'Raise Issue'}</button>
//             <button style={s.ghostBtn} onClick={() => { setShowForm(false); setErr(''); }}>Cancel</button>
//           </div>
//         </div>
//       )}

//       {/* Issue list */}
//       {loading && <div style={{ textAlign: 'center', padding: 30, color: '#6b7280' }}>Loading issues…</div>}
//       {!loading && issues.length === 0 && (
//         <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
//           No issues found. Use "Raise Issue" to document an audit finding.
//         </div>
//       )}
//       {!loading && issues.map(issue => (
//         <div key={issue._id} style={s.issueCard}>
//           <div style={s.issueHeader} onClick={() => setExpanded(expanded === issue._id ? null : issue._id)}>
//             <div style={{ flex: 1, minWidth: 0 }}>
//               <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
//                 <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>#{issue.issueNumber}</span>
//                 <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{issue.title}</span>
//               </div>
//               <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
//                 <span style={{ ...s.badge('grey'), background: `${STATUS_COLORS[issue.status]}18`, border: `1px solid ${STATUS_COLORS[issue.status]}40`, color: STATUS_COLORS[issue.status] }}>
//                   {issue.status?.replace(/_/g, ' ')}
//                 </span>
//                 <span style={{ ...s.badge('grey'), color: SEVERITY_COLORS[issue.severity] }}>{issue.severity}</span>
//                 <span style={s.badge('grey')}>{ISSUE_TYPE_LABELS[issue.type] || issue.type}</span>
//                 {issue.workpaperRef && <span style={s.badge('blue')}>{issue.workpaperRef}</span>}
//                 {issue.amount > 0 && <span style={s.badge('red')}>${issue.amount.toLocaleString()}</span>}
//               </div>
//             </div>
//             <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 10 }}>
//               {(issue.comments || []).filter(c => !c.deletedAt).length > 0 && (
//                 <span style={{ fontSize: 10, color: '#6b7280' }}>💬 {(issue.comments || []).filter(c => !c.deletedAt).length}</span>
//               )}
//               <span style={{ fontSize: 10, color: '#9ca3af' }}>{fmtDate(issue.createdAt)}</span>
//               <span style={{ fontSize: 13, color: '#9ca3af' }}>{expanded === issue._id ? '▲' : '▼'}</span>
//             </div>
//           </div>
//           {expanded === issue._id && (
//             <div style={s.issueBody}>
//               {issue.description && (
//                 <div style={{ fontSize: 12, color: '#4b5563', lineHeight: 1.6, marginBottom: 12, background: '#f9fafb', padding: 8, borderRadius: 4 }}>
//                   {issue.description}
//                 </div>
//               )}
//               <IssueDetail issue={issue} docId={docId} userRole={userRole} onRefresh={load} />
//             </div>
//           )}
//         </div>
//       ))}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // MAIN MODAL
// // ─────────────────────────────────────────────────────────────────────────────
// const WorkpaperModal = ({ docId, onClose }) => {
//   const [tab,            setTab]           = useState('workpapers');
//   const [wpData,         setWpData]        = useState(null);
//   const [signoffData,    setSignoffData]   = useState(null);
//   const [checkboxStates, setCheckboxStates]= useState({});
//   const [mySignature,    setMySignature]   = useState(null);
//   const [currentAdminId, setCurrentAdminId]= useState(null);
//   const [showSigPad,     setShowSigPad]    = useState(false);
//   const [loading,        setLoading]       = useState(true);
//   const [error,          setError]         = useState('');
//   const [signoffErr,     setSignoffErr]    = useState('');

//   // ── Data loaders ────────────────────────────────────────────────────────
//   const loadWorkpapers = useCallback(async () => {
//     try {
//       setLoading(true); setError('');
//       const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers`, { withCredentials: true });
//       setWpData(res.data?.workpaper_package || null);
//     } catch (e) { setError(e?.response?.data?.message || 'Failed to load workpaper package.'); }
//     finally { setLoading(false); }
//   }, [docId]);

//   const loadSignoffs = useCallback(async () => {
//     try {
//       const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers/signoffs`, { withCredentials: true });
//       setSignoffData(res.data || null);
//       if (res.data?.actorId) setCurrentAdminId(res.data.actorId);
//     } catch { /* non-fatal */ }
//   }, [docId]);

//   const loadCheckboxes = useCallback(async () => {
//     try {
//       const res = await axios.get(`${API_URL}/admin/rpa/docs/${docId}/workpapers/checkboxes`, { withCredentials: true });
//       setCheckboxStates(res.data?.checkboxStates || {});
//     } catch { /* non-fatal */ }
//   }, [docId]);

//   const loadMySignature = useCallback(async () => {
//     try {
//       const res = await axios.get(`${API_URL}/admin/profile/signature`, { withCredentials: true });
//       setMySignature(res.data?.signature?.data || null);
//     } catch {
//       setMySignature(null);
//     }
//   }, []);

//   const refresh = useCallback(async () => {
//     await Promise.all([loadWorkpapers(), loadSignoffs(), loadCheckboxes(), loadMySignature()]);
//   }, [loadWorkpapers, loadSignoffs, loadCheckboxes, loadMySignature]);

//   useEffect(() => { refresh(); }, [refresh]);

//   // ── Multi-user checkbox toggle ───────────────────────────────────────────
//   // Optimistic update: add/remove the current user's entry from the checks array.
//   // After a successful PATCH, reload both checkboxes and sign-offs (backend may
//   // have reset the approval chain if a box was unchecked).
//   const handleCheckboxToggle = useCallback(async (checkboxId, sectionId, label, checked) => {
//     // Optimistic update
//     setCheckboxStates(prev => {
//       const next     = { ...prev };
//       const existing = next[checkboxId] || { checkboxId, sectionId, label, checks: [] };

//       if (checked) {
//         const alreadyIn = (existing.checks || []).some(c => c.adminId === currentAdminId);
//         if (!alreadyIn) {
//           next[checkboxId] = {
//             ...existing,
//             checks: [
//               ...(existing.checks || []),
//               { adminId: currentAdminId, adminName: 'You', checkedAt: new Date().toISOString() },
//             ],
//           };
//         }
//       } else {
//         const newChecks = (existing.checks || []).filter(c => c.adminId !== currentAdminId);
//         if (newChecks.length === 0) {
//           delete next[checkboxId];
//         } else {
//           next[checkboxId] = { ...existing, checks: newChecks };
//         }
//       }
//       return next;
//     });

//     try {
//       await axios.patch(
//         `${API_URL}/admin/rpa/docs/${docId}/workpapers/checkboxes/${checkboxId}`,
//         { checked, sectionId, label },
//         { withCredentials: true }
//       );
//       // Reload both — unchecking resets sign-offs on the backend
//       await Promise.all([loadCheckboxes(), loadSignoffs()]);
//     } catch (e) {
//       // Revert optimistic update
//       await Promise.all([loadCheckboxes(), loadSignoffs()]);
//       alert(e?.response?.data?.error || 'Failed to update checkbox');
//     }
//   }, [docId, currentAdminId, loadCheckboxes, loadSignoffs]);

//   // ── Sign-off handlers ────────────────────────────────────────────────────
//   const handleSignoff = useCallback(async (sectionId, signoffType, notes) => {
//     setSignoffErr('');
//     try {
//       await axios.post(`${API_URL}/admin/rpa/docs/${docId}/workpapers/${sectionId}/signoff`, { signoffType, notes }, { withCredentials: true });
//       await loadSignoffs();
//     } catch (e) {
//       const msg = e?.response?.data?.error || 'Sign-off failed.';
//       setSignoffErr(msg); throw e;
//     }
//   }, [docId, loadSignoffs]);

//   const handleRetract = useCallback(async (sectionId, signoffType) => {
//     setSignoffErr('');
//     try {
//       await axios.delete(`${API_URL}/admin/rpa/docs/${docId}/workpapers/${sectionId}/signoff/${signoffType}`, { withCredentials: true });
//       await loadSignoffs();
//     } catch (e) {
//       const msg = e?.response?.data?.error || 'Retraction failed.';
//       setSignoffErr(msg); throw e;
//     }
//   }, [docId, loadSignoffs]);

//   // ── Save signature ───────────────────────────────────────────────────────
//   const handleSaveSignature = useCallback(async (data) => {
//     try {
//       await axios.post(`${API_URL}/admin/profile/signature`, { data, mediaType: 'image/png' }, { withCredentials: true });
//       setMySignature(data);
//       setShowSigPad(false);
//     } catch (e) {
//       alert(e?.response?.data?.error || 'Failed to save signature');
//     }
//   }, []);

//   // ── Derived state ────────────────────────────────────────────────────────
//   const summary         = wpData?.completion_summary || {};
//   const header          = wpData?.package_header     || {};
//   const priorityActions = wpData?.priority_actions_for_auditor || [];
//   const sections        = wpData?.workpaper_sections  || [];
//   const signoffs        = signoffData?.signoffs        || {};
//   const sectionAuth     = signoffData?.sectionAuthority|| {};
//   const userRole        = signoffData?.userRole;
//   const signoffStats    = signoffData?.stats;

//   return (
//     <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
//       <div style={s.modal}>
//         {/* ── Header ── */}
//         <div style={s.header}>
//           <div style={{ flex: 1 }}>
//             <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
//               <h2 style={{ margin: 0, fontSize: 17, color: '#111827' }}>📋 Audit Workpaper Package</h2>
//               {header.entity && <span style={{ fontSize: 12, color: '#6b7280' }}>{header.entity} — {header.period || 'Dec 31, 2024'}</span>}
//               {userRole && <span style={{ ...s.badge('blue'), marginLeft: 4 }}>{AUDIT_ROLE_LABELS[userRole] || userRole}</span>}
//             </div>
//             <div style={{ display: 'flex', marginTop: 10, gap: 0 }}>
//               <button style={s.tab(tab === 'workpapers')} onClick={() => setTab('workpapers')}>
//                 📋 Workpapers {signoffStats ? `(${signoffStats.fullySignedSections}/${signoffStats.totalSections} signed)` : ''}
//               </button>
//               <button style={s.tab(tab === 'issues')} onClick={() => setTab('issues')}>⚠️ Issues</button>
//             </div>
//           </div>
//           <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 10 }}>
//             <button style={s.sigBtn(!!mySignature)} onClick={() => setShowSigPad(true)} title={mySignature ? 'View / update your signature' : 'Set up your signature'}>
//               ✍ {mySignature ? 'My Signature ✓' : 'Set Signature'}
//             </button>
//             <button style={s.refreshBtn} onClick={refresh} disabled={loading}>{loading ? '…' : '↻'}</button>
//             <button style={s.closeBtn} onClick={onClose}>✕</button>
//           </div>
//         </div>

//         <div style={s.body}>
//           {signoffErr && (
//             <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#991b1b', display: 'flex', justifyContent: 'space-between' }}>
//               <span>⚠️ {signoffErr}</span>
//               <button onClick={() => setSignoffErr('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
//             </div>
//           )}

//           {/* ── WORKPAPERS TAB ── */}
//           {tab === 'workpapers' && (
//             <>
//               {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}><div style={{ fontSize: 22 }}>⚙️</div><div style={{ marginTop: 6 }}>Generating workpaper package…</div></div>}
//               {!loading && error && (
//                 <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 16, color: '#991b1b' }}>
//                   <strong>Error:</strong> {error}
//                   <div style={{ marginTop: 8 }}><button onClick={refresh} style={s.actionBtn('#991b1b')}>Retry</button></div>
//                 </div>
//               )}
//               {!loading && !error && wpData && (
//                 <>
//                   <div style={s.summaryGrid}>
//                     {[
//                       { label: 'Sections',        value: summary.total_sections ?? sections.length, color: '#111827' },
//                       { label: 'Complete',         value: summary.complete ?? '—',                   color: '#059669' },
//                       { label: 'Exceptions',       value: summary.has_exceptions ?? '—',             color: summary.has_exceptions > 0 ? '#dc2626' : '#059669' },
//                       { label: 'Incomplete',       value: summary.incomplete ?? '—',                 color: '#f59e0b' },
//                       { label: 'Total Exceptions', value: summary.total_exceptions_identified ?? '—', color: summary.total_exceptions_identified > 0 ? '#dc2626' : '#059669' },
//                       { label: 'Sign-off %',       value: signoffStats ? `${signoffStats.completionPercentage}%` : '—', color: signoffStats?.fullySignedOff ? '#059669' : '#d97706' },
//                       { label: 'Sections Signed',  value: signoffStats ? `${signoffStats.fullySignedSections}/${signoffStats.totalSections}` : '—', color: '#3b82f6' },
//                       { label: 'High Priority',    value: summary.high_priority_actions ?? '—',       color: summary.high_priority_actions > 0 ? '#dc2626' : '#059669' },
//                     ].map(tile => (
//                       <div key={tile.label} style={s.summaryTile}>
//                         <div style={{ fontSize: 20, fontWeight: 700, color: tile.color }}>{tile.value}</div>
//                         <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{tile.label}</div>
//                       </div>
//                     ))}
//                   </div>
//                   {priorityActions.length > 0 && (
//                     <div style={s.priorityBanner}>
//                       <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>🚨 High-Priority Auditor Actions ({priorityActions.length})</div>
//                       {priorityActions.map((action, i) => (
//                         <div key={i} style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 6, padding: 10, marginBottom: 7 }}>
//                           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
//                             <span style={{ fontSize: 12, fontWeight: 600, color: '#991b1b' }}>{action.issue}</span>
//                             <span style={{ fontSize: 10, color: '#6b7280' }}>{action.section_id}</span>
//                           </div>
//                           <div style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}><strong>Area:</strong> {action.account_or_area} &nbsp;|&nbsp; <strong>Section:</strong> {action.section}</div>
//                           <div style={{ fontSize: 11, color: '#374151', marginBottom: 5 }}>{action.detail}</div>
//                           {action.amount && action.amount !== '$0' && <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 5 }}><strong>Amount:</strong> {action.amount}</div>}
//                           <div style={{ fontSize: 11, color: '#7c2d12', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: 6 }}><strong>Action Required:</strong> {action.action_required}</div>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                   {header.purpose && <div style={{ fontSize: 11, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 12, lineHeight: 1.6 }}>{header.purpose}</div>}
//                   <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Workpaper Sections ({sections.length})</div>
//                   {sections.map((section, i) => (
//                     <WorkpaperSection
//                       key={section.section_id || i}
//                       section={section}
//                       sectionSignoffs={signoffs[section.section_id] || {}}
//                       sectionAuthority={sectionAuth[section.section_id] || null}
//                       onSignoff={handleSignoff}
//                       onRetract={handleRetract}
//                       checkboxStates={checkboxStates}
//                       onCheckboxToggle={handleCheckboxToggle}
//                       userSignature={mySignature}
//                       currentAdminId={currentAdminId}
//                     />
//                   ))}
//                   <div style={{ marginTop: 18, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, color: '#6b7280' }}>
//                     Generated by RPA Automated Audit System (Azure Government) · {header.generated_at ? new Date(header.generated_at).toLocaleString() : ''}
//                     {signoffStats && ` · Sign-off: ${signoffStats.fullySignedSections}/${signoffStats.totalSections} sections`}
//                   </div>
//                 </>
//               )}
//             </>
//           )}

//           {/* ── ISSUES TAB ── */}
//           {tab === 'issues' && <IssuesTab docId={docId} userRole={userRole} />}
//         </div>
//       </div>

//       {/* ── SIGNATURE PAD MODAL ── */}
//       {showSigPad && (
//         <SignaturePad
//           currentSignature={mySignature}
//           onSave={handleSaveSignature}
//           onClose={() => setShowSigPad(false)}
//         />
//       )}
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // EXPORTED BUTTON — props unchanged from original
// // ─────────────────────────────────────────────────────────────────────────────
// const GenerateWorkpaperButton = ({ docId, onDone }) => {
//   const [open, setOpen] = useState(false);

//   const handleOpen = () => {
//     setOpen(true);
//     if (onDone) onDone();
//   };

//   return (
//     <>
//       <button style={s.triggerBtn} onClick={handleOpen} title="View Audit Workpapers & Issues">
//         📋 Workpapers
//       </button>
//       {open && <WorkpaperModal docId={docId} onClose={() => setOpen(false)} />}
//     </>
//   );
// };

// export default GenerateWorkpaperButton;